import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { FaSpinner, FaUndo } from "react-icons/fa";
import {
  clampAvatarOffset,
  cropAvatarWithTransform,
  getAvatarCoverScale,
} from "../../utils/cropAvatarImage";
import "./AvatarCropModal.css";

const VIEWPORT_SIZE = 280;
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.01;

export default function AvatarCropModal({
  show,
  imageFile,
  imageSrc,
  originalFile,
  onReloadOriginal,
  onCancel,
  onConfirm,
}) {
  const [activeFile, setActiveFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [imageReady, setImageReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const imgRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    if (!show) {
      setActiveFile(null);
      setImageUrl(null);
      setNaturalSize({ w: 0, h: 0 });
      setOffset({ x: 0, y: 0 });
      setZoom(1);
      setImageReady(false);
      setSaving(false);
      setReloading(false);
      setDragging(false);
      return;
    }
    setActiveFile(imageFile || null);
    setOffset({ x: 0, y: 0 });
    setZoom(1);
    setImageReady(false);
  }, [show, imageFile]);

  useEffect(() => {
    if (!show) return undefined;

    let url = null;
    let ownsUrl = false;

    if (activeFile) {
      url = URL.createObjectURL(activeFile);
      ownsUrl = true;
    } else if (imageSrc && !imageFile) {
      url = imageSrc;
    }

    setImageUrl(url);
    setImageReady(false);

    return () => {
      if (ownsUrl && url) URL.revokeObjectURL(url);
    };
  }, [show, activeFile, imageSrc, imageFile]);

  const applyClampedOffset = useCallback(
    (nextX, nextY, nextZoom = zoom) => {
      const { w, h } = naturalSize;
      if (!w || !h) {
        setOffset({ x: nextX, y: nextY });
        return;
      }
      setOffset(
        clampAvatarOffset(nextX, nextY, w, h, VIEWPORT_SIZE, nextZoom),
      );
    },
    [naturalSize, zoom],
  );

  const handleImageLoad = (e) => {
    const img = e.currentTarget;
    setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    setImageReady(true);
    setOffset(
      clampAvatarOffset(0, 0, img.naturalWidth, img.naturalHeight, VIEWPORT_SIZE, 1),
    );
  };

  const handleZoomChange = (e) => {
    const nextZoom = Number(e.target.value);
    setZoom(nextZoom);
    applyClampedOffset(offset.x, offset.y, nextZoom);
  };

  const onPointerDown = (e) => {
    if (!imageReady || saving || reloading) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: offset.x,
      originY: offset.y,
    };
  };

  const onPointerMove = (e) => {
    if (!dragging || !dragRef.current) return;
    e.preventDefault();
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    applyClampedOffset(
      dragRef.current.originX + dx,
      dragRef.current.originY + dy,
      zoom,
    );
  };

  const endDrag = (e) => {
    if (!dragging) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setDragging(false);
    dragRef.current = null;
  };

  const handleReloadOriginal = async () => {
    if (saving || reloading) return;
    setReloading(true);
    try {
      let nextFile = null;
      if (typeof onReloadOriginal === "function") {
        const loaded = await onReloadOriginal();
        if (loaded instanceof File || loaded instanceof Blob) {
          nextFile = loaded;
        }
      }
      if (!(nextFile instanceof File) && !(nextFile instanceof Blob)) {
        nextFile = originalFile || imageFile || activeFile;
      }
      if (!(nextFile instanceof File) && !(nextFile instanceof Blob)) {
        throw new Error("Imagem original indisponível");
      }
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      setImageReady(false);
      // Nova referência força recriação da object URL
      const type = nextFile.type || "image/jpeg";
      const ext = type.includes("png")
        ? "png"
        : type.includes("webp")
          ? "webp"
          : type.includes("gif")
            ? "gif"
            : "jpg";
      const name =
        (nextFile instanceof File && nextFile.name) ||
        `avatar_original.${ext}`;
      setActiveFile(
        new File([nextFile], name, {
          type,
          lastModified: Date.now(),
        }),
      );
    } catch (err) {
      console.error(err);
      toast.error(
        "Não foi possível recarregar a imagem original. Verifique se ela existe no S3.",
      );
    } finally {
      setReloading(false);
    }
  };

  const handleConfirm = async () => {
    const img = imgRef.current;
    if (!img || !imageReady || saving) return;
    setSaving(true);
    try {
      const sourceName = activeFile?.name || imageFile?.name || "avatar";
      const file = await cropAvatarWithTransform(img, {
        offsetX: offset.x,
        offsetY: offset.y,
        zoom,
        viewportSize: VIEWPORT_SIZE,
        fileName: sourceName,
      });
      onConfirm?.(file);
    } catch (err) {
      console.error(err);
      onConfirm?.(null, err);
    } finally {
      setSaving(false);
    }
  };

  const coverScale =
    naturalSize.w && naturalSize.h
      ? getAvatarCoverScale(naturalSize.w, naturalSize.h, VIEWPORT_SIZE) * zoom
      : 1;
  const displayW = naturalSize.w * coverScale;
  const displayH = naturalSize.h * coverScale;
  const canReloadOriginal = Boolean(originalFile || onReloadOriginal || imageFile);

  return (
    <Modal
      show={show}
      onHide={saving || reloading ? undefined : onCancel}
      centered
      backdrop={saving || reloading ? "static" : true}
      className="acu-avatar-crop-modal"
      aria-labelledby="acu-avatar-crop-title"
    >
      <Modal.Header closeButton={!saving && !reloading}>
        <Modal.Title id="acu-avatar-crop-title">Ajustar foto de perfil</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="acu-avatar-crop-lead">
          Arraste a imagem para centralizar e use o controle para reduzir ou
          ampliar. A área circular será a foto final do convidado.
        </p>

        <div className="acu-avatar-crop-stage">
          <div
            className={`acu-avatar-crop-viewport${dragging ? " acu-avatar-crop-viewport--dragging" : ""}`}
            style={{ width: VIEWPORT_SIZE, height: VIEWPORT_SIZE }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            role="presentation"
          >
            {imageUrl ? (
              <img
                ref={imgRef}
                src={imageUrl}
                alt=""
                className="acu-avatar-crop-image"
                draggable={false}
                crossOrigin={
                  /^https?:\/\//i.test(imageUrl) ? "anonymous" : undefined
                }
                onLoad={handleImageLoad}
                onError={() => setImageReady(false)}
                style={{
                  width: displayW || "auto",
                  height: displayH || "auto",
                  transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                  opacity: imageReady ? 1 : 0,
                }}
              />
            ) : null}
            <div className="acu-avatar-crop-mask" aria-hidden />
            {!imageReady || reloading ? (
              <div className="acu-avatar-crop-loading" aria-live="polite">
                <FaSpinner className="fa-spin" size={22} aria-hidden />
                <span>
                  {reloading ? "Recarregando original…" : "Carregando imagem…"}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        <label className="acu-avatar-crop-zoom" htmlFor="acu-avatar-crop-zoom">
          <span>Zoom</span>
          <input
            id="acu-avatar-crop-zoom"
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={ZOOM_STEP}
            value={zoom}
            onChange={handleZoomChange}
            disabled={!imageReady || saving || reloading}
            aria-valuemin={MIN_ZOOM}
            aria-valuemax={MAX_ZOOM}
            aria-valuenow={zoom}
          />
          <span className="acu-avatar-crop-zoom-value">{zoom.toFixed(2)}x</span>
        </label>

        {canReloadOriginal ? (
          <button
            type="button"
            className="apl-btn apl-btn--ghost acu-avatar-crop-reload"
            onClick={handleReloadOriginal}
            disabled={saving || reloading}
          >
            {reloading ? (
              <FaSpinner className="fa-spin" size={14} aria-hidden />
            ) : (
              <FaUndo size={14} aria-hidden />
            )}
            Recarregar imagem original
          </button>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="acu-avatar-crop-footer">
        <button
          type="button"
          className="apl-btn apl-btn--ghost"
          onClick={onCancel}
          disabled={saving || reloading}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="apl-btn apl-btn--primary"
          onClick={handleConfirm}
          disabled={!imageReady || saving || reloading}
        >
          {saving ? (
            <FaSpinner className="fa-spin" size={14} aria-hidden />
          ) : null}
          {saving ? "Aplicando…" : "Aplicar recorte"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
